import { forwardRef, useEffect, useImperativeHandle, useRef, type InputHTMLAttributes } from "react";

import type { RowSelection } from "@/hooks/useRowSelection";
import { cx } from "@/lib/cx";

import { Td, Th } from "./Table";

type SelectionCheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "aria-label"> & {
  /** Accessible name (the checkbox has no visible label in a table). */
  label: string;
  indeterminate?: boolean;
};

/** A table-cell checkbox: no visible label, optional indeterminate ("some rows on this page") state. */
export const SelectionCheckbox = forwardRef<HTMLInputElement, SelectionCheckboxProps>(function SelectionCheckbox(
  { label, indeterminate = false, className, onClick, ...rest },
  ref,
) {
  const inner = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inner.current as HTMLInputElement);
  useEffect(() => {
    if (inner.current) inner.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={inner}
      type="checkbox"
      aria-label={label}
      className={cx("block size-4 cursor-pointer accent-[var(--accent)] disabled:cursor-not-allowed", className)}
      // Rows open on click: ticking a box must never also open the row.
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      {...rest}
    />
  );
});

/** Header cell that selects or clears the rows of the current page. */
export function SelectPageTh({ selection, ids, noun }: { selection: RowSelection; ids: string[]; noun: string }) {
  const state = selection.pageState(ids);
  return (
    <Th className="w-9 pr-0">
      <SelectionCheckbox
        data-select-page
        label={`Select all ${noun} on this page`}
        checked={state === "all" && ids.length > 0}
        indeterminate={state === "some"}
        disabled={ids.length === 0}
        onChange={() => selection.setRows(ids, state !== "all")}
      />
    </Th>
  );
}

/** Body cell with one row's checkbox. The cell itself also toggles, so the target is easy to hit. */
export function SelectRowTd({ selection, id, name }: { selection: RowSelection; id: string; name: string }) {
  return (
    <Td
      className="w-9 cursor-pointer pr-0"
      onClick={(event) => {
        event.stopPropagation();
        selection.toggle(id);
      }}
    >
      <SelectionCheckbox label={`Select ${name}`} checked={selection.isSelected(id)} onChange={() => selection.toggle(id)} />
    </Td>
  );
}
